/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { JSX } from 'preact';
import {
  ReferenceLine,
  Threshold,
  ThresholdDisplay,
  getVisThresholdColor
} from '../UNSAFE_meterUtils';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { ColorProps } from '../UNSAFE_interpolations/colors';
import { xUnits } from '#UNSAFE_Theme';
import { GetTextDimensionsType } from '#hooks/PRIVATE_useTextDimensions';
import { typographyVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';

/**
 * Validates the min, max, value and step passed to the meter are in appropriate range.
 * @throws { RangeError }
 * @param min The minimum value of the meter.
 * @param max The maximum value of the meter.
 * @param value The metric value of the meter.
 * @param step The step value of the meter.
 */
export function validateRange(
  min: number,
  max: number,
  value: number,
  step: number,
  isRating: boolean,
  isReadonly?: boolean,
  baseline?: number
): void {
  const minStr = isRating ? 'zero' : 'min value';
  if (min > max) {
    throw new RangeError(`The max value must be greater or equal to ${minStr}.`);
  }

  if (value < min || value > max) {
    throw new RangeError(`The value must be between ${minStr} and max value.`);
  }

  if (!isReadonly && step > max - min) {
    throw new RangeError(
      `The step value must be less than the difference of max value and ${minStr}.`
    );
  }

  if (baseline !== undefined) {
    if (baseline < min || baseline > max) {
      throw new RangeError(`Baseline is outside ${minStr} and max value.`);
    }
  }
}

/**
 * Returns the threshold that the current value belongs to. Returns undefined if the current value does not belong to
 * any threshold or if thresholds is undefined.
 * @param value The value in meter.
 * @param thresholds The list of thresholds.
 * @returns The Threshold that current value belongs to.
 */
export function findThreshold(value: number, thresholds?: Threshold[]): Threshold | undefined {
  if (!thresholds) {
    return;
  }
  if (value <= thresholds[0].max) {
    return thresholds[0];
  }
  let minValue = thresholds[0].max;
  for (let i = 1; i < thresholds.length; i++) {
    if (minValue < value && value <= thresholds[i].max) {
      return thresholds[i];
    }
    minValue = thresholds[i].max;
  }
  return;
}

/**
 * Returns the color of track for the meter.
 * @param trackColor The color provided via trackColor prop of the meter.
 * @param thresholdDisplay  Specifies whether current threshold to display in track, indicator or all thresholds in track.
 * @param currentThreshold Current threshold for the meter value.
 * @returns The color of the track for the meter.
 */
function getTrackColor(
  thresholdDisplay: ThresholdDisplay,
  currentThreshold: Threshold | undefined,
  trackColor?: ColorProps['color']
): string | undefined {
  if (thresholdDisplay === 'track' && currentThreshold?.color) {
    return getVisThresholdColor(currentThreshold?.color);
  }
  return trackColor;
}

/**
 * Returns the color of indicator for the meter.
 * @param indicatorColor The color provided via indicatorColor prop of the meter.
 * @param thresholdDisplay  Specifies whether current threshold to display in track, indicator or all thresholds in track.
 * @param currentThreshold Current threshold for the meter value.
 * @returns The color of the indicator of the meter.
 */
function getIndicatorColor(
  thresholdDisplay: ThresholdDisplay,
  currentThreshold: Threshold | undefined,
  indicatorColor?: ColorProps['color']
): string | undefined {
  if (currentThreshold?.color && thresholdDisplay === 'indicator') {
    return getVisThresholdColor(currentThreshold.color);
  }
  return indicatorColor;
}

/**
 * Returns the color of track and indicator of the meter.
 * @param value The value of the meter.
 * @param trackColor The color provided via trackColor prop.
 * @param indicatorColor The color provided via indicatorColor prop.
 * @param thresholdDisplay Specifies whether current threshold to display in track, indicator or all thresholds in track.
 * @param thresholds The thresholds for metric value of the meter.
 * @returns The color of the indicator and track of the meter.
 */
export function getTrackAndIndicatorColor(
  value: number,
  thresholdDisplay: ThresholdDisplay,
  trackColor?: ColorProps['color'],
  indicatorColor?: ColorProps['color'],
  thresholds?: Threshold[]
): {
  trackColor?: ColorProps['color'];
  indicatorColor?: ColorProps['color'];
} {
  const currentThreshold = findThreshold(value, thresholds);
  return {
    trackColor: getTrackColor(thresholdDisplay, currentThreshold, trackColor),
    indicatorColor: getIndicatorColor(thresholdDisplay, currentThreshold, indicatorColor)
  };
}

/**
 * Returns the aria properties of the meter bar.
 * @param value The value of the meter bar.
 * @param min The minimum value of the meter bar.
 * @param max The maximum value of the meter bar.
 * @param ariaLabel The aria label of the meter bar.
 * @param ariaLabelledby The ariaLabelledBy of the meter bar.
 * @param thresholds The thresholds values for the meter bar.
 * @param isDisabled The gauge is disabled or not.
 * @param isReadonly the gauge is readonly or not.
 * @param tooltip The tooltip values for the rating gauge.
 * @returns The aria properties of the meter
 */
export function getMeterAriaProps(
  value: number,
  min: number,
  max: number,
  ariaLabel?: string,
  ariaLabelledby?: string,
  thresholds?: Threshold[],
  isReadonly?: boolean,
  isDisabled?: boolean,
  tooltip?: string
): {
  'aria-label'?: UnpackSignals<JSX.AriaAttributes>['aria-label'];
  'aria-valuenow'?: UnpackSignals<JSX.AriaAttributes>['aria-valuenow'];
  'aria-valuemin'?: UnpackSignals<JSX.AriaAttributes>['aria-valuemin'];
  'aria-valuetext'?: UnpackSignals<JSX.AriaAttributes>['aria-valuetext'];
  'aria-valuemax'?: UnpackSignals<JSX.AriaAttributes>['aria-valuemax'];
  'aria-labelledby'?: UnpackSignals<JSX.AriaAttributes>['aria-labelledby'];
  'aria-disabled'?: UnpackSignals<JSX.AriaAttributes>['aria-disabled'];
  'aria-readonly'?: UnpackSignals<JSX.AriaAttributes>['aria-readonly'];
  role: UnpackSignals<JSX.HTMLAttributes['role']>;
} {
  const currentThreshold = findThreshold(value, thresholds);
  const ariaValueText = currentThreshold?.accessibleLabel
    ? `${value} ${currentThreshold.accessibleLabel}`
    : `${value}`;
  const ariaLabelText = ariaLabel
    ? ariaLabel
    : tooltip && isReadonly && !isDisabled
    ? tooltip
    : undefined;

  if (isReadonly) {
    return {
      'aria-label': ariaLabelText,
      'aria-labelledby': ariaLabelledby,
      'aria-disabled': isDisabled ? true : undefined,
      role: 'img'
    };
  }
  return {
    'aria-label': ariaLabelText,
    'aria-valuenow': value,
    'aria-valuemin': min,
    'aria-valuetext': ariaValueText,
    'aria-valuemax': max,
    'aria-labelledby': ariaLabelledby,
    'aria-disabled': isDisabled ? true : undefined,
    role: 'slider'
  };
}

/**
 * Returns the threshold color that the current value belongs to. Returns component color if the current value does not belong to
 * any threshold or if thresholds is undefined.
 * @param value The value in meter.
 * @param thresholds The list of thresholds.
 * @returns The threshold color that current value belongs to.
 */
export function getThresholdColorFromValue(value: number, color: any, thresholds?: Threshold[]) {
  const currentThreshold = findThreshold(value, thresholds);
  if (currentThreshold?.color) {
    return currentThreshold?.color;
  }
  return color;
}

/**
 * Returns the resolved values for the legend vars.
 */
export const meterVars = [
  {
    tokenVar: xUnits(1),
    key: 'unit',
    cssProp: 'padding'
  }
];

/**
 * Returns the default label font size for given breakpoint.
 */
export function getDefaultReferenceLineLabelFontSize(breakPoint: 'min' | 'xs' | 'sm' | 'md') {
  if (breakPoint === 'min') {
    return typographyVars.body.xs.fontSize;
  } else if (breakPoint === 'xs' || breakPoint === 'sm') {
    return typographyVars.body.md.fontSize;
  } else {
    return typographyVars.body.lg.fontSize;
  }
}

/**
 * Returns if the list of reference line has a label.
 * @param lines
 * @returns
 */
export function hasLabelledReferenceLine(lines?: ReferenceLine[]) {
  return !!lines?.reduce((prev, cur) => {
    return !!cur.label || prev;
  }, false);
}

/**
 * Returns the maximum dimensions of reference lines labels.
 */
export function getMaxRefLabelDims(
  fontSize: string,
  lineHeight: string,
  refLines?: ReferenceLine[],
  getTextDimensions?: GetTextDimensionsType
) {
  if (!hasLabelledReferenceLine(refLines) || !getTextDimensions || !refLines) {
    return { dims: [], maxRefLabelDims: { width: 0, height: 0 } };
  }

  const dims = refLines.map((line) => {
    const { label, labelStyle } = line;
    const dims = label
      ? getTextDimensions(label, { fontSize, lineHeight, ...labelStyle })
      : { width: 0, height: 0 };
    return dims;
  });
  const maxRefLabelDims = dims.reduce(
    (acc, cur) => {
      return {
        width: Math.max(cur.width, acc.width),
        height: Math.max(cur.height, acc.height)
      };
    },
    { width: 0, height: 0 }
  );

  return { dims, maxRefLabelDims };
}

/**
 * Get step adjusted value.
 * @param {number} value Current value
 * @param {number} step Step value
 * @param {number} min Min value
 * @param {number} max Max value
 * @return {number} Adjusted value
 */
export const adjustForStep = (value: number, step: number, min: number, max: number) => {
  if (step && step > 0) {
    const stepNum = value / step;
    const roundedStepNum = Math.round(stepNum * 10) / 10; // Round to 1 decimal.

    if (roundedStepNum < 0.5) {
      return min;
    }
    const adjustedValue = Math.ceil(roundedStepNum) * step;
    return Math.max(Math.min(max, adjustedValue), min);
  }
  return value;
};
